import { useEffect, useRef, useState } from "react";
import { Save, Image as ImageIcon, Plus, Trash2, ImagePlus, GripVertical } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminBanners() {
  const [whatsapp, setWhatsapp] = useState("");
  const [testimonials, setTestimonials] = useState([]);
  const [banners, setBanners] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setWhatsapp(r.data.whatsapp_number || "");
      setTestimonials(r.data.testimonials || []);
      setBanners(r.data.banners || []);
    }).catch(() => {});
  }, []);

  const update = (i, key, val) => setBanners((b) => b.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const remove = (i) => setBanners((b) => b.filter((_, idx) => idx !== i));
  const add = () => setBanners((b) => [...b, { image: "", eyebrow: "", title1: "", title2: "", subtitle: "", link: "/shop" }]);
  const move = (i, dir) => setBanners((b) => {
    const j = i + dir; if (j < 0 || j >= b.length) return b;
    const copy = [...b]; [copy[i], copy[j]] = [copy[j], copy[i]]; return copy;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { whatsapp_number: whatsapp.trim(), banners, testimonials });
      toast.success("Hero banners saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold mb-1">Hero Banners</h1>
      <p className="text-sm text-muted-foreground mb-6">Slides that rotate in the hero at the top of your storefront.</p>

      <div className="flex justify-end mb-4">
        <button onClick={add} className="inline-flex items-center gap-1.5 border border-border rounded-full px-3 py-1.5 text-xs font-medium" data-testid="add-banner-btn"><Plus className="w-3.5 h-3.5" /> Add Slide</button>
      </div>

      <div className="space-y-5">
        {banners.map((b, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 grid sm:grid-cols-[140px_1fr] gap-4" data-testid={`banner-row-${i}`}>
            <BannerImage value={b.image} onChange={(url) => update(i, "image", url)} index={i} />
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Slide {i + 1}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => move(i, -1)} className="p-1.5 text-muted-foreground hover:text-foreground rotate-90" title="Move up" data-testid={`banner-up-${i}`}><GripVertical className="w-4 h-4" /></button>
                  <button onClick={() => remove(i)} className="p-1.5 text-muted-foreground hover:text-destructive" title="Remove" data-testid={`banner-del-${i}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <BF ph="Eyebrow (small label)" value={b.eyebrow} onChange={(v) => update(i, "eyebrow", v)} testid={`banner-eyebrow-${i}`} />
              <div className="grid grid-cols-2 gap-2.5">
                <BF ph="Title line 1" value={b.title1} onChange={(v) => update(i, "title1", v)} testid={`banner-title1-${i}`} />
                <BF ph="Title line 2" value={b.title2} onChange={(v) => update(i, "title2", v)} testid={`banner-title2-${i}`} />
              </div>
              <textarea value={b.subtitle} onChange={(e) => update(i, "subtitle", e.target.value)} placeholder="Subtitle" rows={2} data-testid={`banner-sub-${i}`}
                className="w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm text-foreground outline-none focus:border-primary" />
              <BF ph="Button link (e.g. /shop?category=Camisoles)" value={b.link} onChange={(v) => update(i, "link", v)} testid={`banner-link-${i}`} />
            </div>
          </div>
        ))}
        {banners.length === 0 && <p className="text-sm text-muted-foreground">No slides yet. Add one to show a hero banner.</p>}
      </div>

      <button onClick={save} disabled={saving} className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="save-banners-btn">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Banners"}
      </button>
    </div>
  );
}

const BF = ({ ph, value, onChange, testid }) => (
  <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={ph} data-testid={testid}
    className="w-full rounded-lg bg-secondary border border-input px-3 py-2 text-sm text-foreground outline-none focus:border-primary" />
);

function BannerImage({ value, onChange, index }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <div className="aspect-[3/4] rounded-lg overflow-hidden bg-secondary border border-border">
        {value ? <img src={fileUrl(value)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImageIcon className="w-6 h-6" /></div>}
      </div>
      <button type="button" onClick={() => ref.current.click()} disabled={busy} className="mt-2 w-full inline-flex items-center justify-center gap-1.5 border border-border rounded-lg py-1.5 text-xs font-medium" data-testid={`banner-upload-${index}`}>
        <ImagePlus className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload"}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
    </div>
  );
}
